import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';

import { fetchDebts, addDebt, updateDebt, deleteDebt, payDebt } from '../slices/debtSlice';
import debtService from '../services/debtService';
import { formatAmount } from '../utils/formatHelpers';
import { supportedCurrencies, getCurrencySymbol, detectDefaultCurrency } from '../utils/currencyUtils';
import { useCurrency } from '../contexts/CurrencyContext';
import { useLanguage } from '../contexts/LanguageContext';
import apiClient from '../services/api';
import PagoAutomaticoTarjetas from '../components/PagoAutomaticoTarjetas';
import onboardingService from '../services/onboardingService';
import { useOnboardingRefresh } from '../contexts/OnboardingRefreshContext';

// FIX: el componente Debts.js original mandaba campos en inglés
// (type/amount/monthly_payment/due_date/description/paid) que no existen
// en el schema DebtCreate del backend (nombre, tipo, monto, tasa_interes,
// pago_mensual) — y ni siquiera mandaba `nombre`, que es obligatorio. El
// resultado era que cada intento de "Agregar deuda" fallaba con 422 y el
// paso de onboarding "Deudas" nunca se podía completar. Esta versión
// reemplaza el componente con un flujo de diálogo igual al de
// CuentasPage, usando los nombres de campo reales del backend.

// 17 sep 2026 (pedido de Augusto: "se puede tener una version completa en
// ingles?"): esta pantalla no tenia ningun mecanismo de idioma. tiposDeuda
// se vuelve una funcion de la moneda actual (isSpanish, calculado en el
// componente) en vez de una constante de módulo fija en español, porque
// sus labels se muestran directo en Select/Chip más abajo.
const makeTiposDeuda = (isSpanish) => [
  { value: 'tarjeta', label: isSpanish ? 'Tarjeta de crédito' : 'Credit card' },
  { value: 'prestamo_personal', label: isSpanish ? 'Préstamo personal' : 'Personal loan' },
  { value: 'prestamo_estudiantil', label: isSpanish ? 'Préstamo estudiantil' : 'Student loan' },
  { value: 'hipoteca', label: isSpanish ? 'Hipoteca' : 'Mortgage' },
  { value: 'otro', label: isSpanish ? 'Otro' : 'Other' },
];

// La moneda default de una deuda nueva es la moneda local del usuario
// (detectDefaultCurrency, según su país de perfil / navegador / zona
// horaria — ver currencyUtils.js), no USD a secas — para un cliente en
// Perú eso significa que el selector arranca en PEN.
const makeEmptyDebt = (moneda = 'USD') => ({
  nombre: '',
  // 25 sep 2026 (Augusto: "la deuda debe de incluir la opción de
  // registrar la entidad que hace el préstamo"). El campo existía en la
  // base y lo llenaba el importador, pero no había forma de escribirlo a
  // mano: una hipoteca o un préstamo familiar quedaban sin acreedor.
  //
  // Además de informativo, es lo que usa PCM para reconocer que una
  // tarjeta importada y una deuda ya cargada son la misma (ver
  // _find_or_create_debt).
  entidad: '',
  // 25 sep 2026 (Augusto: "colocar la fecha de corte de cada tarjeta en
  // Conciliación y en Deuda"). Vive en la cuenta de la tarjeta, no en la
  // deuda, pero se edita acá: desde que las tarjetas salieron de
  // Cuentas, este es el único lugar donde el usuario las ve.
  dia_cierre: '',
  tipo: 'tarjeta',
  monto: 0,
  moneda,
  tasa_interes: '',
  plazo_meses: '',
  pago_mensual: '',
  dia_vencimiento: '',
});

// Calculadora de cuota mensual (31 jul 2026) — fórmula de amortización
// estándar (sistema francés, el que usan bancos y casas financieras para
// préstamos con cuota fija). Con tasa 0% cae al caso simple: monto /
// plazo. Devuelve null si falta monto o plazo (no se puede calcular).
const calcularCuotaMensual = (monto, tasaAnualPct, plazoMeses) => {
  const P = Number(monto) || 0;
  const n = parseInt(plazoMeses, 10) || 0;
  if (!P || !n) return null;
  const rMensual = (Number(tasaAnualPct) || 0) / 100 / 12;
  if (rMensual === 0) return P / n;
  const factor = Math.pow(1 + rMensual, -n);
  return (P * rMensual) / (1 - factor);
};

// Lista de monedas para el selector — mismo catálogo que usa el resto de
// la app (currencyUtils.js), con USD y PEN primero porque son las más
// comunes para los clientes de Darwin Optima en Perú.
const currencyOptions = ['USD', 'PEN', ...Object.keys(supportedCurrencies).filter((c) => c !== 'USD' && c !== 'PEN')];

// 30 oct 2026 (Augusto, con todas las tarjetas en S/ 0.00: "¿qué pasó
// aquí?"). El monto de una tarjeta con extracto se CALCULA: saldo del
// último extracto menos los pagos posteriores al corte. Un número
// calculado que no dice de dónde sale no se puede revisar, así que se
// muestra debajo, con cada pago restado (fecha, monto y descripción).
const DesgloseDeuda = ({ d, simbolo, isSpanish, onCambio }) => {
  // 30 oct 2026 (Augusto: "el search no funciona!!!!!"): para quitar un
  // pago mal registrado no debería hacer falta ir a buscarlo a
  // Transacciones. "pagos después" abre la lista y cada uno se puede
  // borrar desde acá (si es una transferencia, se van los dos lados y la
  // cuenta de origen recupera su saldo).
  const [abierto, setAbierto] = useState(false);
  const [borrando, setBorrando] = useState(null);
  const [errorBorrar, setErrorBorrar] = useState(null);
  const g = d?.desglose;
  if (!g) return null;
  // Un préstamo cargado a mano (Préstamo Mamá) no tiene extracto y está
  // bien así: el aviso solo tiene sentido en una tarjeta.
  if (g.sin_extracto && d.tipo !== 'tarjeta') return null;
  if (g.sin_extracto) {
    const texto = {
      sin_cuenta: isSpanish ? 'sin cuenta vinculada' : 'no linked account',
      cuenta_sin_extractos: isSpanish ? `sin extracto importado (cuenta #${g.account_id})` : 'no imported statement',
      otra_moneda: isSpanish
        ? `extracto solo en ${(g.monedas_con_extracto || []).join(', ')} (cuenta #${g.account_id})`
        : `statement only in ${(g.monedas_con_extracto || []).join(', ')}`,
    }[g.motivo];
    return (
      <Typography variant="caption" sx={{ display: 'block', color: 'warning.dark', whiteSpace: 'nowrap' }}>
        {texto}{isSpanish ? ' · monto manual' : ' · manual amount'}
      </Typography>
    );
  }
  const fmt = (n) => Number(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fecha = (iso) => {
    const [y, m, dd] = iso.split('-').map(Number);
    return new Date(y, m - 1, dd).toLocaleDateString('es-PE', { day: '2-digit', month: 'short' });
  };
  const totalPagos = g.pagos_posteriores.reduce((a, p) => a + p.monto, 0);
  return (
    <>
    <Tooltip
      arrow
      title={
        g.pagos_posteriores.length === 0
          ? (isSpanish ? 'Ningún pago después del corte.' : 'No payments after closing.')
          : (
            <Box>
              {g.pagos_posteriores.map((p) => (
                <div key={p.id}>
                  {fecha(p.fecha)} · {simbolo} {fmt(p.monto)} · {p.descripcion || '—'}
                  {p.pendiente ? ` (${p.pendiente})` : ''}
                </div>
              ))}
            </Box>
          )
      }
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', cursor: 'help', whiteSpace: 'nowrap' }}>
        {isSpanish ? 'extracto ' : 'statement '}{fecha(g.fecha_corte)}: {simbolo}{fmt(g.saldo_extracto)}
        {totalPagos > 0 && (
          <>
            <br />
            <Box
              component="span"
              onClick={() => { setErrorBorrar(null); setAbierto(true); }}
              sx={{ textDecoration: 'underline', cursor: 'pointer', color: 'primary.main' }}
            >
              {isSpanish ? '− pagos después: ' : '− later payments: '}{simbolo}{fmt(totalPagos)} ({g.pagos_posteriores.length})
            </Box>
          </>
        )}
      </Typography>
    </Tooltip>
    <Dialog open={abierto} onClose={() => setAbierto(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{isSpanish ? 'Pagos registrados después del corte' : 'Payments after closing'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isSpanish
            ? `Estos pagos se restan del extracto del ${fecha(g.fecha_corte)}. Si alguno no ocurrió, bórralo: si es una transferencia se borran los dos lados y la cuenta de origen recupera su saldo.`
            : 'These payments are subtracted from the statement. Delete any that did not happen.'}
        </Typography>
        {errorBorrar && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{errorBorrar}</Typography>}
        {g.pagos_posteriores.map((p) => (
          <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>{simbolo} {fmt(p.monto)} · {fecha(p.fecha)}</Typography>
              <Typography variant="caption" color="text.secondary">{p.descripcion || '—'}</Typography>
            </Box>
            <Button
              size="small"
              color="error"
              disabled={borrando === p.id}
              onClick={async () => {
                if (!window.confirm(isSpanish ? `¿Borrar este pago de ${simbolo} ${fmt(p.monto)}?` : 'Delete this payment?')) return;
                setBorrando(p.id);
                setErrorBorrar(null);
                try {
                  await apiClient.delete(`/transactions/${p.id}`);
                  setAbierto(false);
                  if (onCambio) onCambio();
                } catch (err) {
                  setErrorBorrar(err.response?.data?.detail || (isSpanish ? 'No se pudo borrar. Revisa tu conexión e inténtalo de nuevo.' : 'Could not delete.'));
                } finally {
                  setBorrando(null);
                }
              }}
            >
              {borrando === p.id ? (isSpanish ? 'Borrando...' : 'Deleting...') : (isSpanish ? 'Borrar' : 'Delete')}
            </Button>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAbierto(false)}>{isSpanish ? 'Cerrar' : 'Close'}</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

const DebtsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const debts = useSelector((state) => state.debts.list);
  const loading = useSelector((state) => state.debts.loading);
  const error = useSelector((state) => state.debts.error);
  const user = useSelector((state) => state.auth.user);
  const { exchangeRates } = useCurrency();
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const tiposDeuda = useMemo(() => makeTiposDeuda(isSpanish), [isSpanish]);
  const tipoLabel = (value) => tiposDeuda.find((t) => t.value === value)?.label || value || '—';

  // 17 sep 2026 (Augusto: "coloquemos los recordatorios como una opción,
  // no mandatorio"): el correo de "tu cuota vence pronto" existe en el
  // backend desde hace rato (/admin/debt-reminders/send, lo dispara una
  // tarea diaria) pero nunca tuvo interruptor -- y se vendía en el plan
  // Personal como si el cliente lo tuviera. Ahora es opt-in: el default
  // de User.debt_reminders_enabled es false, así que nadie recibe correos
  // que no pidió. Vive acá, en Deudas, y no en Perfil, porque es donde el
  // cliente está pensando en sus vencimientos.
  const [recordatorios, setRecordatorios] = useState(null); // null = todavía no se sabe
  const [guardandoRecordatorios, setGuardandoRecordatorios] = useState(false);

  useEffect(() => {
    let vigente = true;
    apiClient
      .get('/auth/me')
      .then((res) => {
        if (vigente) setRecordatorios(Boolean(res.data?.debt_reminders_enabled));
      })
      .catch(() => {
        // Si no se puede leer la preferencia, se oculta el interruptor en
        // vez de mostrarlo en un estado que quizá no es el real.
        if (vigente) setRecordatorios(null);
      });
    return () => {
      vigente = false;
    };
  }, []);

  const handleToggleRecordatorios = async (event) => {
    const nuevo = event.target.checked;
    const anterior = recordatorios;
    setRecordatorios(nuevo); // optimista: el interruptor responde al toque
    setGuardandoRecordatorios(true);
    try {
      await apiClient.put('/users/profile', { debt_reminders_enabled: nuevo });
    } catch (err) {
      setRecordatorios(anterior); // se revierte si el guardado falló
    } finally {
      setGuardandoRecordatorios(false);
    }
  };
  const defaultCurrency = useMemo(() => detectDefaultCurrency(user?.country), [user?.country]);

  useEffect(() => {
    dispatch(fetchDebts());
  }, [dispatch]);

  // 10 ago 2026 (Augusto: "no aparece un indicador si va a ingresar más
  // cuentas o deudas o no"): se lee el estado de onboarding acá mismo
  // (además del checklist de la barra lateral) para poder mostrar, justo
  // debajo de la tabla, un aviso confirmando que este paso ya quedó
  // completo con la opción de seguir agregando o continuar -- antes, en
  // cuanto se agregaba la primera deuda, la página se quedaba muda sin
  // ninguna confirmación. refreshOnboarding() también avisa a
  // NotionLayout (vía OnboardingRefreshContext) para que el checklist de
  // la barra lateral se actualice sin esperar a un cambio de ruta.
  const triggerOnboardingRefresh = useOnboardingRefresh();
  const [onboardingData, setOnboardingData] = useState(null);
  const refreshOnboarding = () => {
    onboardingService
      .getOnboardingData()
      .then((data) => setOnboardingData(data))
      .catch(() => {});
    triggerOnboardingRefresh();
  };
  useEffect(() => {
    refreshOnboarding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "No tengo deudas" (31 jul 2026): antes, el paso "Deudas" del onboarding
  // (ver OnboardingSidebar.js) solo se marcaba hecho si el usuario tenía al
  // menos una deuda real guardada (backend: debts_completed = bool(user.debts)
  // en onboarding.py). Un cliente sin deudas quedaba atrapado para SIEMPRE
  // en el checklist de 3 pasos — nunca veía Transacciones, Calendario ni el
  // resto del menú, porque NotionLayout.js solo muestra el menú completo
  // cuando profile+accounts+debts están en true. Este botón llama al mismo
  // endpoint de onboarding que ya existía (/onboarding/update-state) pero
  // marcando debts_completed=true a mano, sin exigir una deuda real.
  const [markingNoDebts, setMarkingNoDebts] = useState(false);
  const handleNoDebts = async () => {
    setMarkingNoDebts(true);
    try {
      await onboardingService.updateOnboardingState({ progress: { debts: true } });
      // Navegar (no solo re-render) para que NotionLayout, que vuelve a
      // pedir /onboarding/data en cada cambio de ruta, detecte el cambio y
      // muestre el menú completo en vez del checklist.
      navigate('/dashboard');
    } catch (err) {
      // No crítico: si falla, el botón simplemente se puede volver a
      // intentar — no se pierde nada ni se bloquea el resto de la página.
    } finally {
      setMarkingNoDebts(false);
    }
  };

  // FIX: el resumen del slice (state.debts.summary) suma "monto" a secas
  // entre deudas, sin importar la moneda — mezclaba soles y dólares como si
  // fueran el mismo número. Acá se convierte cada deuda a USD usando la
  // tasa de cambio en vivo (exchangeRates, base USD desde CurrencyContext)
  // antes de sumar, para que "Deuda total" sea un número real.
  // 29 ago 2026 (pedido de Augusto: "arriba colócame el tipo de cambio que
  // utilizas, y permite que yo pueda cambiarlo"): CurrencyContext trae la
  // tasa en vivo (open.er-api.com, base USD) pero no expone ninguna forma
  // de sobrescribirla, y ese contexto es compartido por toda la app -- no
  // conviene tocarlo solo para esta pantalla. Acá se guarda un override
  // LOCAL a Deudas en localStorage (sobrevive recargas y el refresco
  // horario del contexto); "usar tasa en vivo" borra el override y vuelve
  // a depender de exchangeRates tal cual.
  const RATE_OVERRIDE_KEY = 'pcm_debts_tasa_manual';
  const [manualRates, setManualRates] = useState(() => {
    try {
      const raw = window.localStorage.getItem(RATE_OVERRIDE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [editingRateFor, setEditingRateFor] = useState(null); // código de moneda en edición, o null
  const [rateDraft, setRateDraft] = useState('');

  const guardarManualRates = (next) => {
    setManualRates(next);
    try {
      window.localStorage.setItem(RATE_OVERRIDE_KEY, JSON.stringify(next));
    } catch {
      // localStorage no disponible (modo privado, etc.) -- el override
      // simplemente no persiste entre recargas, no es crítico.
    }
  };
  const getRate = (moneda) => manualRates[moneda] ?? exchangeRates?.[moneda];
  const handleStartEditRate = (moneda) => {
    setEditingRateFor(moneda);
    setRateDraft(String(getRate(moneda) ?? ''));
  };
  const handleSaveRate = (moneda) => {
    const valor = parseFloat(rateDraft);
    if (valor > 0) guardarManualRates({ ...manualRates, [moneda]: valor });
    setEditingRateFor(null);
  };
  const handleResetRate = (moneda) => {
    const next = { ...manualRates };
    delete next[moneda];
    guardarManualRates(next);
    setEditingRateFor(null);
  };
  // Monedas distintas a USD presentes en las deudas del usuario -- son las
  // únicas para las que tiene sentido mostrar/editar una tasa de cambio acá.
  const monedasNoUSD = useMemo(
    () => Array.from(new Set(debts.map((d) => d.moneda).filter((m) => m && m !== 'USD'))),
    [debts]
  );

  const toUSD = (amount, moneda) => {
    const num = Number(amount) || 0;
    if (!moneda || moneda === 'USD') return num;
    const rate = getRate(moneda);
    return rate ? num / rate : num; // sin tasa disponible: no se puede convertir, se deja tal cual
  };
  const totalDebtUSD = debts.reduce((sum, d) => sum + toUSD(d.monto, d.moneda), 0);
  const monthlyPaymentUSD = debts.reduce((sum, d) => sum + toUSD(d.pago_mensual, d.moneda), 0);

  // 29 ago 2026 (pedido de Augusto: "añade el total de deuda por tipo de
  // moneda también"): total SIN convertir, agrupado tal cual está guardada
  // cada deuda -- complementa (no reemplaza) el total ya convertido a USD.
  const totalPorMoneda = useMemo(() => {
    const totales = {};
    debts.forEach((d) => {
      const moneda = d.moneda || 'USD';
      totales[moneda] = (totales[moneda] || 0) + (Number(d.monto) || 0);
    });
    return totales;
  }, [debts]);

  // 28 ago 2026 (pedido de Augusto: "en Deuda, solo mostrar 1 tarjeta (1
  // deuda), y que cada columna muestre la deuda en Soles o Dólares"): el
  // backend guarda una tarjeta con saldo en dos monedas como DOS filas Debt
  // separadas -- por diseño (ver la nota en app/models/debt.py) -- pero eso
  // hacía que la MISMA tarjeta física apareciera dos veces en esta tabla:
  // una fila en soles, otra en dólares (ej. "BBVA - Visa Signature" x2).
  // Acá se agrupan en el frontend por tarjeta física, con el mismo
  // criterio que ya usa el backend para reconocerla al importar un estado
  // de cuenta (ver _find_or_create_debt en statements.py): entidad +
  // últimos 4 dígitos si existen, si no entidad + nombre. Una deuda sin
  // entidad NI últimos dígitos (préstamo, hipoteca cargada a mano) no se
  // agrupa con nada -- sigue siendo su propia fila.
  const normalizarTexto = (s) => (s || '').trim().toLowerCase();
  const claveTarjeta = (d) => {
    // 21 oct 2026 (auditoría A-1): el backend ya manda account_id, la
    // clave real de la tarjeta. Dos filas con la misma cuenta son la misma
    // tarjeta en dos monedas, sin importar cómo se llamen. Lo de abajo
    // queda solo para deudas sin cuenta.
    if (d.account_id) return `ac|${d.account_id}`;
    if (d.entidad && d.ultimos_digitos) return `ed|${normalizarTexto(d.entidad)}|${d.ultimos_digitos}`;
    if (d.entidad && d.nombre) return `en|${normalizarTexto(d.entidad)}|${normalizarTexto(d.nombre)}`;
    return `id|${d.id}`;
  };
  const tarjetasAgrupadas = useMemo(() => {
    const grupos = new Map();
    debts.forEach((d) => {
      const clave = claveTarjeta(d);
      if (!grupos.has(clave)) grupos.set(clave, []);
      grupos.get(clave).push(d);
    });
    return Array.from(grupos.entries()).map(([clave, items]) => {
      const debtPEN = items.find((d) => normalizarTexto(d.moneda) === 'pen') || null;
      const debtUSD = items.find((d) => normalizarTexto(d.moneda) === 'usd') || null;
      const otras = items.filter((d) => d !== debtPEN && d !== debtUSD);
      const representativa = debtPEN || debtUSD || items[0];
      return {
        clave,
        ids: items.map((d) => d.id),
        nombre: representativa.nombre,
        tipo: representativa.tipo,
        entidad: representativa.entidad || '',
        ultimos_digitos: representativa.ultimos_digitos || '',
        debtPEN,
        debtUSD,
        otras,
        tasa_interes: debtPEN?.tasa_interes ?? debtUSD?.tasa_interes ?? representativa.tasa_interes ?? null,
        plazo_meses: debtPEN?.plazo_meses ?? debtUSD?.plazo_meses ?? representativa.plazo_meses ?? null,
        dia_vencimiento: debtPEN?.dia_vencimiento ?? debtUSD?.dia_vencimiento ?? representativa.dia_vencimiento ?? null,
        // 25 sep 2026 (Augusto: "no registra la fecha de corte en las
        // tarjetas cuando las meto manualmente"). El backend lo devolvía
        // en cada deuda y esta agrupación no lo copiaba, así que la
        // tarjeta mostraba "sin día de cierre" pasara lo que pasara --
        // incluso después de guardarlo bien.
        dia_cierre: debtPEN?.dia_cierre ?? debtUSD?.dia_cierre ?? representativa.dia_cierre ?? null,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debts]);

  // 26 sep 2026 (Augusto: "aparecen 2 cuentas American Express" -- ••6369
  // y ••6359, contra un extracto que dice ...6359; y después: "aún no me
  // aparece otra Amex en Deuda, esto solo se sigue viendo en
  // Transacciones").
  //
  // Lo segundo fue la pista: hay DOS tarjetas como cuenta y UNA sola como
  // deuda, porque los dos emparejadores del importador usaban criterios
  // distintos. Por eso esto NO se calcula agrupando las deudas de esta
  // pantalla -- se pregunta al backend, que mira las cuentas, que es
  // donde de verdad están partidas (ver tarjetas_duplicadas).
  const [tarjetasParecidas, setTarjetasParecidas] = useState([]);
  const cargarDuplicadas = useCallback(() => {
    debtService.duplicadas()
      .then((r) => setTarjetasParecidas(r.data || []))
      .catch(() => setTarjetasParecidas([]));
  }, []);
  useEffect(() => { cargarDuplicadas(); }, [cargarDuplicadas]);

  const [openUnir, setOpenUnir] = useState(false);
  const [uniendo, setUniendo] = useState(false);
  const [errorUnir, setErrorUnir] = useState(null);
  const [grupoUnir, setGrupoUnir] = useState(null);
  const [destinoUnir, setDestinoUnir] = useState(null);
  const abrirUnir = (grupo) => {
    setGrupoUnir(grupo);
    // Por defecto se queda la que más movimientos tiene: la lectura mala
    // suele ser la del extracto suelto, con dos o tres consumos.
    const mejor = [...grupo.tarjetas].sort((a, b) => (b.movimientos || 0) - (a.movimientos || 0))[0];
    setDestinoUnir(mejor?.account_id ?? null);
    setErrorUnir(null);
    setOpenUnir(true);
  };
  const confirmarUnir = async () => {
    if (!grupoUnir || !destinoUnir) return;
    setUniendo(true);
    setErrorUnir(null);
    try {
      for (const t of grupoUnir.tarjetas) {
        if (t.account_id === destinoUnir) continue;
        await debtService.unirCuentas(t.account_id, destinoUnir);
      }
      await dispatch(fetchDebts());
      cargarDuplicadas();
      setOpenUnir(false);
    } catch (err) {
      setErrorUnir(
        err.response?.data?.detail ||
          (isSpanish ? 'No se pudieron unir las tarjetas.' : 'Could not merge the cards.')
      );
    } finally {
      setUniendo(false);
    }
  };

  const [openNew, setOpenNew] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteGroup, setDeleteGroup] = useState(null);
  // 4 sep 2026 (Augusto: "registré una transferencia... no se ve una
  // reducción de deuda"): diálogo "Registrar pago" -- una tarjeta
  // agrupada puede tener fila en Soles Y en Dólares a la vez, así que
  // hay que elegir a cuál de las dos (idDebt) se le resta el pago si
  // ambas existen.
  const [openPago, setOpenPago] = useState(false);
  const [pagoGrupo, setPagoGrupo] = useState(null); // { nombre, opciones: [{id, moneda, monto}] }
  const [pagoIdDebt, setPagoIdDebt] = useState('');
  const [pagoMonto, setPagoMonto] = useState('');
  const [pagoError, setPagoError] = useState(null);
  const [pagoGuardando, setPagoGuardando] = useState(false);

  const [nuevaDeuda, setNuevaDeuda] = useState(() => makeEmptyDebt(defaultCurrency));
  // Ver comentario de tarjetasAgrupadas más arriba -- grupoEditando ya no
  // es un solo Debt sino UNA TARJETA, con hasta dos filas reales detrás
  // (idPEN/idUSD, null si esa moneda no existe todavía para esta tarjeta).
  const [grupoEditando, setGrupoEditando] = useState(null);

  const handleOpenNew = () => setOpenNew(true);
  const handleCloseNew = () => {
    setOpenNew(false);
    setNuevaDeuda(makeEmptyDebt(defaultCurrency));
  };

  const handleOpenEdit = (grupo) => {
    setGrupoEditando({
      clave: grupo.clave,
      entidad: grupo.entidad,
      ultimos_digitos: grupo.ultimos_digitos,
      nombre: grupo.nombre,
      tipo: grupo.tipo || 'otro',
      idPEN: grupo.debtPEN?.id ?? null,
      idUSD: grupo.debtUSD?.id ?? null,
      montoPEN: grupo.debtPEN?.monto ?? '',
      montoUSD: grupo.debtUSD?.monto ?? '',
      tasa_interes: grupo.tasa_interes ?? '',
      plazo_meses: grupo.plazo_meses ?? '',
      pago_mensualPEN: grupo.debtPEN?.pago_mensual ?? '',
      pago_mensualUSD: grupo.debtUSD?.pago_mensual ?? '',
      dia_vencimiento: grupo.dia_vencimiento ?? '',
      // 25 sep 2026: faltaba. El diálogo abría el campo del día de cierre
      // vacío aunque la tarjeta ya tuviera uno, así que parecía que nunca
      // se había guardado -- y guardar sin tocarlo lo dejaba igual, lo que
      // confirmaba la impresión.
      dia_cierre: grupo.dia_cierre ?? '',
    });
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setGrupoEditando(null);
  };

  const handleDeleteConfirmOpen = (grupo) => {
    setDeleteGroup({ ids: grupo.ids, nombre: grupo.nombre });
    setOpenDelete(true);
  };
  const handleDeleteConfirmClose = () => {
    setOpenDelete(false);
    setDeleteGroup(null);
  };

  const handleOpenPago = (grupo) => {
    const opciones = [
      grupo.debtPEN ? { id: grupo.debtPEN.id, moneda: 'PEN', monto: grupo.debtPEN.monto } : null,
      grupo.debtUSD ? { id: grupo.debtUSD.id, moneda: 'USD', monto: grupo.debtUSD.monto } : null,
    ].filter(Boolean);
    setPagoGrupo({ nombre: grupo.nombre, opciones });
    setPagoIdDebt(opciones.length === 1 ? String(opciones[0].id) : '');
    setPagoMonto('');
    setPagoError(null);
    setOpenPago(true);
  };
  const handleClosePago = () => {
    setOpenPago(false);
    setPagoGrupo(null);
    setPagoIdDebt('');
    setPagoMonto('');
    setPagoError(null);
  };
  const handleGuardarPago = async () => {
    const monto = parseFloat(pagoMonto);
    if (!pagoIdDebt || !(monto > 0)) return;
    setPagoGuardando(true);
    setPagoError(null);
    try {
      await dispatch(payDebt({ id: Number(pagoIdDebt), monto })).unwrap();
      handleClosePago();
    } catch (err) {
      setPagoError(err?.detail || (isSpanish ? 'No se pudo registrar el pago.' : 'Could not record the payment.'));
    } finally {
      setPagoGuardando(false);
    }
  };

  const numericFields = ['monto', 'tasa_interes', 'pago_mensual'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNuevaDeuda((prev) => ({
      ...prev,
      [name]: numericFields.includes(name) ? value : value,
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setGrupoEditando((prev) => ({ ...prev, [name]: value }));
  };

  // 28 ago 2026: entidad/ultimos_digitos se agregan al payload SOLO cuando
  // vienen explícitos en `data` (ver camposIdentidad en
  // handleUpdateGroupSubmit) -- para "Agregar Nueva Deuda" y para
  // actualizar una fila ya existente no hace falta mandarlos (el backend
  // usa exclude_unset, así que omitirlos deja esos campos tal cual están
  // guardados); pero al CREAR la fila de la segunda moneda de una tarjeta
  // que antes solo tenía una, sí hay que mandarlos explícitamente para que
  // quede reconocida como la misma tarjeta física (acá mismo y en el
  // próximo estado de cuenta que se importe).
  const buildPayload = (data) => ({
    nombre: data.nombre?.trim(),
    tipo: data.tipo || null,
    ...(data.entidad !== undefined ? { entidad: data.entidad || null } : {}),
    // 25 sep 2026: antes, un dia_cierre vacío se omitía del payload y el
    // backend (exclude_unset) lo dejaba como estaba -- o sea, borrarlo era
    // imposible: el campo se vaciaba en pantalla y al recargar volvía el
    // valor viejo. Ahora vacío se manda como null, que sí lo borra.
    ...(data.dia_cierre !== undefined
      ? {
          dia_cierre:
            data.dia_cierre === '' || data.dia_cierre === null
              ? null
              : parseInt(data.dia_cierre, 10),
        }
      : {}),
    ...(data.ultimos_digitos !== undefined ? { ultimos_digitos: data.ultimos_digitos || null } : {}),
    monto: parseFloat(data.monto) || 0,
    moneda: data.moneda || 'USD',
    tasa_interes: data.tasa_interes === '' ? null : parseFloat(data.tasa_interes),
    plazo_meses: data.plazo_meses === '' || data.plazo_meses == null ? null : parseInt(data.plazo_meses, 10),
    pago_mensual: data.pago_mensual === '' ? null : parseFloat(data.pago_mensual),
    dia_vencimiento: data.dia_vencimiento === '' || data.dia_vencimiento == null ? null : parseInt(data.dia_vencimiento, 10),
  });

  // Cuota estimada e interés total con los términos ingresados en cada
  // diálogo — se recalcula en cada tecleo (useMemo), no requiere ida y
  // vuelta al backend porque es una fórmula pura.
  const cuotaEstimadaNueva = useMemo(
    () => calcularCuotaMensual(nuevaDeuda.monto, nuevaDeuda.tasa_interes, nuevaDeuda.plazo_meses),
    [nuevaDeuda.monto, nuevaDeuda.tasa_interes, nuevaDeuda.plazo_meses]
  );
  const interesTotalNueva = useMemo(() => {
    if (cuotaEstimadaNueva == null || !nuevaDeuda.plazo_meses) return null;
    return cuotaEstimadaNueva * parseInt(nuevaDeuda.plazo_meses, 10) - (Number(nuevaDeuda.monto) || 0);
  }, [cuotaEstimadaNueva, nuevaDeuda.plazo_meses, nuevaDeuda.monto]);

  // 28 ago 2026: cuota estimada por moneda -- grupoEditando puede tener
  // monto en soles Y en dólares a la vez, así que se calculan por
  // separado (comparten tasa_interes/plazo_meses, que sí son atributos de
  // la tarjeta como un todo). null si esa moneda no tiene monto cargado.
  const cuotaEstimadaEditandoPEN = useMemo(
    () => (grupoEditando ? calcularCuotaMensual(grupoEditando.montoPEN, grupoEditando.tasa_interes, grupoEditando.plazo_meses) : null),
    [grupoEditando]
  );
  const cuotaEstimadaEditandoUSD = useMemo(
    () => (grupoEditando ? calcularCuotaMensual(grupoEditando.montoUSD, grupoEditando.tasa_interes, grupoEditando.plazo_meses) : null),
    [grupoEditando]
  );

  const handleCreateDebt = async () => {
    if (!nuevaDeuda.nombre?.trim()) return; // nombre es obligatorio en el backend
    await dispatch(addDebt(buildPayload(nuevaDeuda)));
    handleCloseNew();
    // Ver comentario junto a refreshOnboarding más arriba -- sin esto, el
    // checklist de la barra lateral y el aviso de "paso completo" de esta
    // misma página se quedaban desactualizados hasta el próximo cambio de
    // ruta.
    refreshOnboarding();
  };

  // 28 ago 2026: guarda los cambios de UNA TARJETA (grupoEditando), que
  // puede implicar hasta dos operaciones reales -- actualiza la fila
  // Soles y/o la fila Dólares que ya existían, y crea la fila que falte
  // si el usuario le cargó un monto a una moneda que esta tarjeta todavía
  // no tenía (ver comentario junto a buildPayload sobre camposIdentidad).
  const handleUpdateGroupSubmit = async () => {
    if (!grupoEditando?.nombre?.trim()) return;
    const camposComunes = {
      nombre: grupoEditando.nombre,
      tipo: grupoEditando.tipo,
      tasa_interes: grupoEditando.tasa_interes,
      plazo_meses: grupoEditando.plazo_meses,
      dia_vencimiento: grupoEditando.dia_vencimiento,
      // 25 sep 2026 (Augusto: "nada, no registra la fecha de corte en las
      // tarjetas cuando las meto manualmente").
      //
      // dia_cierre estaba en camposIdentidad, y camposIdentidad solo se
      // usa al CREAR una fila nueva -- nunca al actualizar una existente.
      // O sea: editabas una tarjeta que ya estaba, escribías el día, y el
      // campo ni siquiera salía del navegador. El comentario que había
      // acá decía "se manda siempre"; era falso.
      //
      // Va en camposComunes, que sí se manda en los dos casos.
      dia_cierre: grupoEditando.dia_cierre,
    };
    const camposIdentidad = {
      entidad: grupoEditando.entidad,
      ultimos_digitos: grupoEditando.ultimos_digitos,
    };
    const tareas = [];
    const tienePEN = grupoEditando.montoPEN !== '' && grupoEditando.montoPEN != null;
    const tieneUSD = grupoEditando.montoUSD !== '' && grupoEditando.montoUSD != null;
    if (grupoEditando.idPEN) {
      tareas.push(
        dispatch(
          updateDebt({
            id: grupoEditando.idPEN,
            data: buildPayload({ ...camposComunes, moneda: 'PEN', monto: grupoEditando.montoPEN || 0, pago_mensual: grupoEditando.pago_mensualPEN }),
          })
        )
      );
    } else if (tienePEN) {
      tareas.push(
        dispatch(
          addDebt(
            buildPayload({ ...camposComunes, ...camposIdentidad, moneda: 'PEN', monto: grupoEditando.montoPEN, pago_mensual: grupoEditando.pago_mensualPEN })
          )
        )
      );
    }
    if (grupoEditando.idUSD) {
      tareas.push(
        dispatch(
          updateDebt({
            id: grupoEditando.idUSD,
            data: buildPayload({ ...camposComunes, moneda: 'USD', monto: grupoEditando.montoUSD || 0, pago_mensual: grupoEditando.pago_mensualUSD }),
          })
        )
      );
    } else if (tieneUSD) {
      tareas.push(
        dispatch(
          addDebt(
            buildPayload({ ...camposComunes, ...camposIdentidad, moneda: 'USD', monto: grupoEditando.montoUSD, pago_mensual: grupoEditando.pago_mensualUSD })
          )
        )
      );
    }
    await Promise.all(tareas);
    handleCloseEdit();
  };

  // 28 ago 2026: elimina TODAS las filas reales detrás de la tarjeta
  // agrupada (una o dos, según tenga solo una moneda o ambas).
  const handleDeleteDebt = async () => {
    if (deleteGroup?.ids?.length) {
      await Promise.all(deleteGroup.ids.map((id) => dispatch(deleteDebt(id))));
      handleDeleteConfirmClose();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {isSpanish ? 'Deudas' : 'Debts'}
      </Typography>

      {loading && (
        <Box sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {isSpanish ? 'Cargando deudas...' : 'Loading debts...'}
          </Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ my: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography variant="body2" color="error.main">
            {isSpanish
              ? `Error al cargar deudas: ${typeof error === 'string' ? error : 'ocurrió un problema.'}`
              : `Error loading debts: ${typeof error === 'string' ? error : 'something went wrong.'}`}
          </Typography>
        </Box>
      )}

      {/* "No tengo deudas": sin este aviso, un cliente sin deudas nunca
          podía avanzar más allá de este paso del onboarding (ver comentario
          junto a handleNoDebts) — se muestra solo mientras la lista está
          vacía y ya terminó de cargar, para no aparecer de golpe antes de
          saber si el cliente ya tiene deudas guardadas. */}
      {!loading && debts.length === 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleNoDebts}
              disabled={markingNoDebts}
              startIcon={markingNoDebts ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {markingNoDebts ? (isSpanish ? 'Guardando...' : 'Saving...') : (isSpanish ? 'No tengo deudas, continuar' : "I don't have debts, continue")}
            </Button>
          }
        >
          {isSpanish
            ? 'Si no tienes préstamos ni tarjetas de crédito que registrar, puedes continuar sin agregar ninguna.'
            : "If you don't have any loans or credit cards to record, you can continue without adding one."}
        </Alert>
      )}

      {/* Resumen */}
      <Card sx={{ mb: 4, bgcolor: 'error.light', color: 'error.contrastText' }}>
        <CardContent>
          {monedasNoUSD.length > 0 && (
            <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.35)' }}>
              {monedasNoUSD.map((moneda) => (
                <Box key={moneda} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="body2">
                    {isSpanish ? 'Tipo de cambio' : 'Exchange rate'}: 1 USD = {getRate(moneda) ? `${getRate(moneda)} ${moneda}` : (isSpanish ? 'no disponible' : 'not available')}
                    {manualRates[moneda] != null ? (isSpanish ? ' (manual)' : ' (manual)') : ''}
                  </Typography>
                  {editingRateFor === moneda ? (
                    <>
                      <TextField
                        size="small"
                        type="number"
                        value={rateDraft}
                        onChange={(e) => setRateDraft(e.target.value)}
                        autoFocus
                        sx={{ width: 100, bgcolor: 'background.paper', borderRadius: 1 }}
                        inputProps={{ step: '0.0001', min: 0 }}
                      />
                      <Button size="small" color="inherit" onClick={() => handleSaveRate(moneda)}>
                        {isSpanish ? 'Guardar' : 'Save'}
                      </Button>
                      <Button size="small" color="inherit" onClick={() => setEditingRateFor(null)}>
                        {isSpanish ? 'Cancelar' : 'Cancel'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Tooltip title={isSpanish ? 'Cambiar tipo de cambio' : 'Change exchange rate'}>
                        <IconButton size="small" color="inherit" onClick={() => handleStartEditRate(moneda)}>
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      {manualRates[moneda] != null && (
                        <Button size="small" color="inherit" onClick={() => handleResetRate(moneda)}>
                          {isSpanish ? 'Usar tasa en vivo' : 'Use live rate'}
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">{isSpanish ? 'Deuda total (convertida a USD)' : 'Total debt (converted to USD)'}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                ${formatAmount(totalDebtUSD)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">{isSpanish ? 'Pago mensual total (convertido a USD)' : 'Total monthly payment (converted to USD)'}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                ${formatAmount(monthlyPaymentUSD)}
              </Typography>
            </Grid>
          </Grid>
          {Object.keys(totalPorMoneda).length > 1 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.35)' }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {isSpanish ? 'Total de deuda por moneda (sin convertir)' : 'Total debt by currency (unconverted)'}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {Object.entries(totalPorMoneda)
                  .map(([moneda, total]) => `${getCurrencySymbol(moneda)}${formatAmount(total)} ${moneda}`)
                  .join('   ·   ')}
              </Typography>
            </Box>
          )}
          {debts.some((d) => d.moneda && d.moneda !== 'USD') && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, opacity: 0.85 }}>
              {isSpanish ? (
                <>
                  Incluye deudas en otras monedas, convertidas a USD con la tasa de cambio
                  {monedasNoUSD.some((m) => manualRates[m] != null) ? ' que definiste manualmente' : ' actual'}.
                </>
              ) : (
                <>
                  Includes debts in other currencies, converted to USD with
                  {monedasNoUSD.some((m) => manualRates[m] != null) ? ' the exchange rate you set manually' : ' the current exchange rate'}.
                </>
              )}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Interruptor de recordatorios por correo (opt-in, ver el comentario
          junto a `recordatorios` arriba). Solo aparece si el cliente tiene
          al menos una deuda registrada -- ofrecerle recordatorios a quien
          no tiene deudas no significa nada. */}
      {recordatorios !== null && debts.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={recordatorios}
                onChange={handleToggleRecordatorios}
                disabled={guardandoRecordatorios}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {isSpanish
                    ? 'Avisarme por correo cuando una cuota esté por vencer'
                    : 'Email me when a payment is about to be due'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isSpanish
                    ? 'Recibes un correo 14, 5 y 1 día antes, y el mismo día del vencimiento. Solo para las deudas que tengan día de vencimiento registrado. Puedes apagarlo cuando quieras.'
                    : 'You get an email 14, 5 and 1 day before, and on the due date itself. Only for debts that have a due day saved. You can turn it off whenever you want.'}
                </Typography>
              </Box>
            }
          />
        </Paper>
      )}

      {/* 30 oct 2026: pago programado de tarjetas al 100% (ver el componente). */}
      <PagoAutomaticoTarjetas isSpanish={isSpanish} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button variant="contained" size="small" onClick={handleOpenNew}>
          {isSpanish ? 'Agregar Deuda' : 'Add Debt'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {tarjetasAgrupadas.length} {isSpanish ? (tarjetasAgrupadas.length === 1 ? 'deuda' : 'deudas') : (tarjetasAgrupadas.length === 1 ? 'debt' : 'debts')}
        </Typography>
      </Box>

      {/* 26 sep 2026 (Augusto: "aparecen 2 cuentas American Express").
          PCM señala; unir lo decide él, porque es irreversible y dos
          tarjetas del mismo banco con el mismo nombre suelen ser dos
          tarjetas de verdad. */}
      {tarjetasParecidas.map((par) => (
        <Alert
          key={par.clave}
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => abrirUnir(par)}>
              {isSpanish ? 'Revisar' : 'Review'}
            </Button>
          }
        >
          {isSpanish ? (
            <>
              <strong>{par.tarjetas.length} tarjetas se llaman igual</strong> — {par.nombre}
              {' ('}
              {par.tarjetas.map((t) => (t.ultimos_digitos ? `••••${t.ultimos_digitos}` : '?')).join(', ')}
              {'). '}
              Si los dígitos se parecen mucho, es probable que sea una sola leída mal en un extracto
              escaneado. Compáralos con tu estado de cuenta y únelas si son la misma.
            </>
          ) : (
            <>
              <strong>{par.tarjetas.length} cards share the same name</strong> — {par.nombre}
              {' ('}
              {par.tarjetas.map((t) => (t.ultimos_digitos ? `••••${t.ultimos_digitos}` : '?')).join(', ')}
              {'). '}
              If the digits are nearly identical it is probably one card misread in a scanned
              statement. Check against your statement and merge them if they are the same.
            </>
          )}
        </Alert>
      ))}

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small" aria-label={isSpanish ? 'tabla de deudas' : 'debts table'} sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ minWidth: 220 }}>{isSpanish ? 'Nombre' : 'Name'}</TableCell>
              <TableCell>{isSpanish ? 'Tipo' : 'Type'}</TableCell>
              <TableCell align="right">{isSpanish ? 'Monto (Soles)' : 'Amount (Soles)'}</TableCell>
              <TableCell align="right">{isSpanish ? 'Monto (Dólares)' : 'Amount (Dollars)'}</TableCell>
              <TableCell align="right">{isSpanish ? 'Tasa de interés' : 'Interest rate'}</TableCell>
              <TableCell align="center">{isSpanish ? 'Plazo (meses)' : 'Term (months)'}</TableCell>
              <TableCell align="right">{isSpanish ? 'Pago mensual' : 'Monthly payment'}</TableCell>
              <TableCell align="center">{isSpanish ? 'Vence (día)' : 'Due (day)'}</TableCell>
              <TableCell align="center">{isSpanish ? 'Acciones' : 'Actions'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tarjetasAgrupadas.map((grupo) => (
              <TableRow key={grupo.clave} hover>
                <TableCell component="th" scope="row" sx={{ minWidth: 220 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', whiteSpace: 'nowrap' }}>
                    {grupo.nombre}
                  </Typography>
                  {/* 25 sep 2026: a quién le debes. Sin esto, tres
                      tarjetas de tres bancos distintos se ven casi
                      iguales en esta tabla. */}
                  {grupo.entidad && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {grupo.entidad}
                    </Typography>
                  )}
                  {/* 25 sep 2026: el ciclo de facturación. Sin él, la
                      conciliación corta a fin de mes y los períodos no
                      coinciden con el estado de cuenta. */}
                  {grupo.tipo === 'tarjeta' && (
                    <Typography
                      variant="caption"
                      color={grupo.dia_cierre ? 'text.secondary' : 'warning.main'}
                      sx={{ display: 'block' }}
                    >
                      {grupo.dia_cierre
                        ? (isSpanish ? `Cierra el ${grupo.dia_cierre} de cada mes` : `Closes on the ${grupo.dia_cierre}`)
                        : (isSpanish ? 'Sin fecha de corte' : 'No closing day')}
                    </Typography>
                  )}
                  {grupo.ultimos_digitos && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      •••• {grupo.ultimos_digitos}
                    </Typography>
                  )}
                  {grupo.otras.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      + {grupo.otras.map((d) => `${d.moneda} ${getCurrencySymbol(d.moneda)}${formatAmount(d.monto)}`).join(', ')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={tipoLabel(grupo.tipo)} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="right">
                  {grupo.debtPEN ? (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                        S/{formatAmount(grupo.debtPEN.monto)}
                      </Typography>
                      <DesgloseDeuda d={grupo.debtPEN} simbolo="S/" isSpanish={isSpanish} onCambio={() => dispatch(fetchDebts())} />
                    </>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell align="right">
                  {grupo.debtUSD ? (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                        ${formatAmount(grupo.debtUSD.monto)}
                      </Typography>
                      <DesgloseDeuda d={grupo.debtUSD} simbolo="$" isSpanish={isSpanish} onCambio={() => dispatch(fetchDebts())} />
                    </>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell align="right">
                  {grupo.tasa_interes != null ? `${Number(grupo.tasa_interes).toFixed(2)}%` : '—'}
                </TableCell>
                <TableCell align="center">
                  {grupo.plazo_meses != null ? grupo.plazo_meses : '—'}
                </TableCell>
                <TableCell align="right">
                  {/* 28 ago 2026: si la tarjeta tiene cuota en las dos
                      monedas a la vez se muestran ambas en dos líneas; si
                      solo tiene una, esa sola. */}
                  {(() => {
                    const lineas = [
                      grupo.debtPEN?.pago_mensual != null ? `S/${formatAmount(grupo.debtPEN.pago_mensual)}` : null,
                      grupo.debtUSD?.pago_mensual != null ? `$${formatAmount(grupo.debtUSD.pago_mensual)}` : null,
                    ].filter(Boolean);
                    if (lineas.length === 0) return '—';
                    return lineas.map((linea, idx) => (
                      <Typography key={idx} variant="body2">
                        {linea}
                      </Typography>
                    ));
                  })()}
                </TableCell>
                <TableCell align="center">
                  {grupo.dia_vencimiento != null ? grupo.dia_vencimiento : '—'}
                </TableCell>
                <TableCell align="center">
                  <ButtonGroup size="small" variant="text">
                    <Tooltip title={isSpanish ? 'Registrar pago' : 'Record payment'}>
                      <IconButton size="small" color="success" onClick={() => handleOpenPago(grupo)}>
                        <PaymentIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isSpanish ? 'Editar deuda' : 'Edit debt'}>
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(grupo)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isSpanish ? 'Eliminar deuda' : 'Delete debt'}>
                      <IconButton size="small" color="error" onClick={() => handleDeleteConfirmOpen(grupo)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ButtonGroup>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 10 ago 2026 (Augusto: "no aparece un indicador si va a ingresar
          más cuentas o deudas o no"): confirmación explícita de que este
          paso ya quedó completo, con la opción de seguir agregando (botón
          "Agregar Deuda" de arriba) o continuar -- solo mientras el
          onboarding general no esté completo, para no molestar a un
          cliente que ya usa la página normalmente día a día. */}
      {!loading && debts.length > 0 && onboardingData && !onboardingData.completed && (
        <Alert
          severity="success"
          sx={{ mb: 4 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(onboardingData.accounts_completed ? '/dashboard' : '/accounts')}
            >
              {onboardingData.accounts_completed ? (isSpanish ? 'Ir al Tablero' : 'Go to Dashboard') : (isSpanish ? 'Continuar' : 'Continue')}
            </Button>
          }
        >
          {isSpanish
            ? 'Este paso quedó listo. ¿Tienes otra deuda? Agrégala con el botón "Agregar Deuda" de arriba -- si ya terminaste, continúa.'
            : 'This step is done. Have another debt? Add it with the "Add Debt" button above -- once you\'re finished, continue.'}
        </Alert>
      )}

      {/* Diálogo nueva deuda */}
      <Dialog open={openNew} onClose={handleCloseNew} fullWidth maxWidth="sm">
        <DialogTitle>{isSpanish ? 'Agregar Nueva Deuda' : 'Add New Debt'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="nombre"
            label={isSpanish ? 'Nombre de la deuda' : 'Debt name'}
            type="text"
            fullWidth
            required
            value={nuevaDeuda.nombre}
            onChange={handleChange}
            sx={{ mb: 2, mt: 1 }}
            helperText={isSpanish ? 'Por ejemplo: BCP - Visa, Préstamo vehicular...' : 'For example: BCP - Visa, Auto loan...'}
          />

          <TextField
            margin="dense"
            name="entidad"
            label={isSpanish ? '¿Quién te prestó?' : 'Who lent it to you?'}
            type="text"
            fullWidth
            value={nuevaDeuda.entidad || ''}
            onChange={handleChange}
            sx={{ mb: 2 }}
            helperText={isSpanish
              ? 'El banco, la financiera o la persona. Sirve para agrupar esta deuda con la tarjeta cuando importes su estado de cuenta.'
              : 'The bank, lender or person. Used to match this debt with the card when you import its statement.'}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="tipo-deuda-label">{isSpanish ? 'Tipo de deuda' : 'Debt type'}</InputLabel>
            <Select
              labelId="tipo-deuda-label"
              name="tipo"
              value={nuevaDeuda.tipo}
              label={isSpanish ? 'Tipo de deuda' : 'Debt type'}
              onChange={handleChange}
            >
              {tiposDeuda.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
            <TextField
              margin="dense"
              name="monto"
              label={isSpanish ? 'Monto adeudado' : 'Amount owed'}
              type="number"
              fullWidth
              value={nuevaDeuda.monto}
              onChange={handleChange}
            />
            <FormControl sx={{ mt: 1, minWidth: 110 }}>
              <InputLabel id="moneda-nueva-label">{isSpanish ? 'Moneda' : 'Currency'}</InputLabel>
              <Select
                labelId="moneda-nueva-label"
                name="moneda"
                value={nuevaDeuda.moneda}
                label={isSpanish ? 'Moneda' : 'Currency'}
                onChange={handleChange}
              >
                {currencyOptions.map((code) => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {isSpanish
              ? '¿Esta tarjeta tiene saldo en soles Y en dólares en el mismo periodo? Regístrala como dos deudas separadas (una por moneda) para que el total se calcule bien.'
              : 'Does this card have a balance in soles AND dollars in the same period? Register it as two separate debts (one per currency) so the total is calculated correctly.'}
          </Typography>

          <TextField
            margin="dense"
            name="tasa_interes"
            label={isSpanish ? 'Tasa de interés anual (%)' : 'Annual interest rate (%)'}
            type="number"
            fullWidth
            value={nuevaDeuda.tasa_interes}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            name="plazo_meses"
            label={isSpanish ? 'Plazo del préstamo (meses)' : 'Loan term (months)'}
            type="number"
            fullWidth
            inputProps={{ min: 1 }}
            value={nuevaDeuda.plazo_meses}
            onChange={handleChange}
            helperText={isSpanish ? 'Con esto, la tasa y el monto, calculamos la cuota mensual con intereses.' : "With this, the rate, and the amount, we'll calculate the monthly payment with interest."}
            sx={{ mb: 2 }}
          />

          {cuotaEstimadaNueva != null && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                {isSpanish ? 'Cuota mensual estimada' : 'Estimated monthly payment'}:{' '}
                <strong>{getCurrencySymbol(nuevaDeuda.moneda)}{formatAmount(cuotaEstimadaNueva)}</strong>
              </Typography>
              {interesTotalNueva != null && (
                <Typography variant="caption" color="text.secondary">
                  {isSpanish ? 'Interés total del préstamo' : 'Total loan interest'}: {getCurrencySymbol(nuevaDeuda.moneda)}{formatAmount(interesTotalNueva)}
                  {' '}· {isSpanish ? 'Total a pagar' : 'Total to pay'}: {getCurrencySymbol(nuevaDeuda.moneda)}{formatAmount((Number(nuevaDeuda.monto) || 0) + interesTotalNueva)}
                </Typography>
              )}
              <Box sx={{ mt: 0.5 }}>
                <Button
                  size="small"
                  onClick={() => setNuevaDeuda((prev) => ({ ...prev, pago_mensual: cuotaEstimadaNueva.toFixed(2) }))}
                >
                  {isSpanish ? 'Usar cuota estimada' : 'Use estimated payment'}
                </Button>
              </Box>
            </Box>
          )}

          <TextField
            margin="dense"
            name="pago_mensual"
            label={isSpanish ? 'Pago mensual' : 'Monthly payment'}
            type="number"
            fullWidth
            value={nuevaDeuda.pago_mensual}
            onChange={handleChange}
            helperText={isSpanish ? 'Puedes ajustar este valor a mano si tu cuota real incluye seguros u otros cargos.' : 'You can adjust this value by hand if your real payment includes insurance or other fees.'}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            name="dia_vencimiento"
            label={isSpanish ? 'Día de vencimiento (1-31)' : 'Due day (1-31)'}
            type="number"
            fullWidth
            inputProps={{ min: 1, max: 31 }}
            value={nuevaDeuda.dia_vencimiento}
            onChange={handleChange}
            helperText={isSpanish ? 'Día del mes en que vence el pago — habilita las alertas en el Calendario.' : 'Day of the month the payment is due — enables alerts in the Calendar.'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNew}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleCreateDebt} variant="contained" color="primary" disabled={!nuevaDeuda.nombre?.trim()}>
            {isSpanish ? 'Guardar' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo editar deuda */}
      <Dialog open={openEdit} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>{isSpanish ? 'Editar Deuda' : 'Edit Debt'}</DialogTitle>
        <DialogContent>
          {grupoEditando && (
            <>
              <TextField
                autoFocus
                margin="dense"
                name="nombre"
                label={isSpanish ? 'Nombre de la deuda' : 'Debt name'}
                type="text"
                fullWidth
                required
                value={grupoEditando.nombre}
                onChange={handleEditChange}
                sx={{ mb: 2, mt: 1 }}
              />

              {/* 25 sep 2026 (Augusto: "la deuda debe de incluir la
                  opción de registrar la entidad que hace el préstamo").
                  El campo existía en la base y lo llenaba el importador,
                  pero no había forma de escribirlo a mano. */}
              <TextField
                margin="dense"
                name="entidad"
                label={isSpanish ? '¿Quién te prestó?' : 'Who lent it to you?'}
                type="text"
                fullWidth
                value={grupoEditando.entidad || ''}
                onChange={handleEditChange}
                sx={{ mb: 2 }}
                helperText={isSpanish
                  ? 'El banco, la financiera o la persona.'
                  : 'The bank, lender or person.'}
              />

              {grupoEditando.tipo === 'tarjeta' && (
                <TextField
                  margin="dense"
                  name="dia_cierre"
                  label={isSpanish ? 'Día de cierre del ciclo (1-31)' : 'Statement closing day (1-31)'}
                  type="number"
                  fullWidth
                  inputProps={{ min: 1, max: 31 }}
                  value={grupoEditando.dia_cierre ?? ''}
                  onChange={handleEditChange}
                  sx={{ mb: 2 }}
                  helperText={isSpanish
                    ? 'El día en que la tarjeta cierra su estado de cuenta (distinto del día de pago). Sin esto, Conciliación corta los períodos a fin de mes y no coinciden con tu extracto.'
                    : "The day the card closes its statement (not the payment due day). Without it, Reconciliation cuts periods at month end and they won't match your statement."}
                />
              )}

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="tipo-deuda-edit-label">{isSpanish ? 'Tipo de deuda' : 'Debt type'}</InputLabel>
                <Select
                  labelId="tipo-deuda-edit-label"
                  name="tipo"
                  value={grupoEditando.tipo || 'otro'}
                  label={isSpanish ? 'Tipo de deuda' : 'Debt type'}
                  onChange={handleEditChange}
                >
                  {tiposDeuda.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 28 ago 2026: ya no hay un selector de Moneda -- una
                  tarjeta puede tener saldo en las dos monedas a la vez, así
                  que hay un campo de monto por cada una (dejar en blanco la
                  que no aplique). Si esta tarjeta ya tenía una fila real en
                  esa moneda se actualiza; si no tenía y se le pone un
                  monto, se crea una nueva. */}
              <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
                <TextField
                  margin="dense"
                  name="montoPEN"
                  label={isSpanish ? 'Monto adeudado (Soles)' : 'Amount owed (Soles)'}
                  type="number"
                  fullWidth
                  value={grupoEditando.montoPEN}
                  onChange={handleEditChange}
                />
                <TextField
                  margin="dense"
                  name="montoUSD"
                  label={isSpanish ? 'Monto adeudado (Dólares)' : 'Amount owed (Dollars)'}
                  type="number"
                  fullWidth
                  value={grupoEditando.montoUSD}
                  onChange={handleEditChange}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {isSpanish
                  ? 'Completa el monto en cada moneda en la que esta tarjeta tenga saldo. Deja en blanco la que no aplique.'
                  : "Fill in the amount for each currency this card carries a balance in. Leave blank whichever doesn't apply."}
              </Typography>

              <TextField
                margin="dense"
                name="tasa_interes"
                label={isSpanish ? 'Tasa de interés anual (%)' : 'Annual interest rate (%)'}
                type="number"
                fullWidth
                value={grupoEditando.tasa_interes ?? ''}
                onChange={handleEditChange}
                sx={{ mb: 2 }}
              />

              <TextField
                margin="dense"
                name="plazo_meses"
                label={isSpanish ? 'Plazo del préstamo (meses)' : 'Loan term (months)'}
                type="number"
                fullWidth
                inputProps={{ min: 1 }}
                value={grupoEditando.plazo_meses ?? ''}
                onChange={handleEditChange}
                helperText={isSpanish ? 'Con esto, la tasa y el monto, calculamos la cuota mensual con intereses.' : "With this, the rate, and the amount, we'll calculate the monthly payment with interest."}
                sx={{ mb: 2 }}
              />

              {(cuotaEstimadaEditandoPEN != null || cuotaEstimadaEditandoUSD != null) && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                  {cuotaEstimadaEditandoPEN != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: cuotaEstimadaEditandoUSD != null ? 0.5 : 0 }}>
                      <Typography variant="body2">
                        {isSpanish ? 'Cuota estimada (Soles)' : 'Estimated payment (Soles)'}: <strong>S/{formatAmount(cuotaEstimadaEditandoPEN)}</strong>
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setGrupoEditando((prev) => ({ ...prev, pago_mensualPEN: cuotaEstimadaEditandoPEN.toFixed(2) }))}
                      >
                        {isSpanish ? 'Usar' : 'Use'}
                      </Button>
                    </Box>
                  )}
                  {cuotaEstimadaEditandoUSD != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {isSpanish ? 'Cuota estimada (Dólares)' : 'Estimated payment (Dollars)'}: <strong>${formatAmount(cuotaEstimadaEditandoUSD)}</strong>
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setGrupoEditando((prev) => ({ ...prev, pago_mensualUSD: cuotaEstimadaEditandoUSD.toFixed(2) }))}
                      >
                        {isSpanish ? 'Usar' : 'Use'}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  margin="dense"
                  name="pago_mensualPEN"
                  label={isSpanish ? 'Pago mensual (Soles)' : 'Monthly payment (Soles)'}
                  type="number"
                  fullWidth
                  value={grupoEditando.pago_mensualPEN ?? ''}
                  onChange={handleEditChange}
                />
                <TextField
                  margin="dense"
                  name="pago_mensualUSD"
                  label={isSpanish ? 'Pago mensual (Dólares)' : 'Monthly payment (Dollars)'}
                  type="number"
                  fullWidth
                  value={grupoEditando.pago_mensualUSD ?? ''}
                  onChange={handleEditChange}
                />
              </Box>

              <TextField
                margin="dense"
                name="dia_vencimiento"
                label={isSpanish ? 'Día de vencimiento (1-31)' : 'Due day (1-31)'}
                type="number"
                fullWidth
                inputProps={{ min: 1, max: 31 }}
                value={grupoEditando.dia_vencimiento ?? ''}
                onChange={handleEditChange}
                helperText={isSpanish ? 'Día del mes en que vence el pago — habilita las alertas en el Calendario.' : 'Day of the month the payment is due — enables alerts in the Calendar.'}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleUpdateGroupSubmit} variant="contained" color="primary" disabled={!grupoEditando?.nombre?.trim()}>
            {isSpanish ? 'Guardar Cambios' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Registrar pago (4 sep 2026, Augusto: "registré una transferencia
          de mi cuenta BCP Ahorros dolares a la BCP Visa saphiro y no se ve
          una reducción de deuda"): resta el monto pagado directo de
          Debt.monto -- ver comentario en schemas/debt.py::DebtPago sobre
          por qué hacía falta esto (ninguna transacción, sin importar el
          tipo, tocaba antes este número). */}
      <Dialog open={openPago} onClose={handleClosePago} fullWidth maxWidth="xs">
        <DialogTitle>{isSpanish ? 'Registrar pago' : 'Record payment'}</DialogTitle>
        <DialogContent>
          {pagoGrupo && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {pagoGrupo.nombre}
              </Typography>
              {pagoGrupo.opciones.length > 1 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="pago-moneda-label">{isSpanish ? 'Moneda' : 'Currency'}</InputLabel>
                  <Select
                    labelId="pago-moneda-label"
                    label={isSpanish ? 'Moneda' : 'Currency'}
                    value={pagoIdDebt}
                    onChange={(e) => setPagoIdDebt(e.target.value)}
                  >
                    {pagoGrupo.opciones.map((op) => (
                      <MenuItem key={op.id} value={String(op.id)}>
                        {op.moneda} — {isSpanish ? 'debe' : 'owes'} {getCurrencySymbol(op.moneda)}{formatAmount(op.monto)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {pagoGrupo.opciones.length === 1 && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {isSpanish ? 'Saldo actual' : 'Current balance'}: {getCurrencySymbol(pagoGrupo.opciones[0].moneda)}
                  {formatAmount(pagoGrupo.opciones[0].monto)}
                </Typography>
              )}
              <TextField
                autoFocus
                margin="dense"
                label={isSpanish ? 'Monto pagado' : 'Amount paid'}
                type="number"
                fullWidth
                value={pagoMonto}
                onChange={(e) => setPagoMonto(e.target.value)}
                helperText={isSpanish ? 'Se resta directo del saldo de la deuda. Si pagas de más, el saldo queda en 0.' : "It's subtracted directly from the debt's balance. If you overpay, the balance is left at 0."}
              />
              {pagoError && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                  {pagoError}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePago}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button
            onClick={handleGuardarPago}
            variant="contained"
            color="primary"
            disabled={!pagoIdDebt || !(parseFloat(pagoMonto) > 0) || pagoGuardando}
          >
            {pagoGuardando ? (isSpanish ? 'Guardando...' : 'Saving...') : (isSpanish ? 'Registrar pago' : 'Record payment')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de eliminación */}
      <Dialog open={openDelete} onClose={handleDeleteConfirmClose}>
        <DialogTitle>
          {isSpanish ? '¿Eliminar la deuda y todos sus movimientos?' : 'Delete the debt and all its transactions?'}
        </DialogTitle>
        <DialogContent>
          {/* 25 sep 2026 (Augusto: "siempre colocar mensaje que al borrar
              se elimina todo y se pierde todo"). Antes decía solo "no se
              puede deshacer" y no mencionaba que se lleva por delante
              todos los consumos importados de la tarjeta. */}
          <Alert severity="error" sx={{ mb: 2 }}>
            {isSpanish ? (
              <>
                Se borra TODO y no hay forma de recuperarlo:{' '}
                {deleteGroup?.nombre ? <strong>{deleteGroup.nombre}</strong> : 'esta deuda'}
                {deleteGroup?.ids?.length > 1 ? ` (${deleteGroup.ids.length} registros, soles y dólares)` : ''}
                {' '}y todos los movimientos importados de sus extractos.
              </>
            ) : (
              <>
                EVERYTHING is deleted with no way to recover it:{' '}
                {deleteGroup?.nombre ? <strong>{deleteGroup.nombre}</strong> : 'this debt'}
                {deleteGroup?.ids?.length > 1 ? ` (${deleteGroup.ids.length} records, soles and dollars)` : ''}
                {' '}and every transaction imported from its statements.
              </>
            )}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {isSpanish
              ? 'Si vas a volver a importar los mismos extractos, borrar primero es la forma correcta — pero comprueba que la deuda desaparezca de la lista antes de importar de nuevo.'
              : 'If you are going to re-import the same statements, deleting first is the right move — but check that the debt disappears from the list before importing again.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleDeleteDebt} color="error" autoFocus>
            {isSpanish ? 'Eliminar' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* 26 sep 2026: unir dos tarjetas (ver unir_deudas en el backend). */}
      <Dialog open={openUnir} onClose={() => setOpenUnir(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isSpanish ? 'Unir dos tarjetas en una' : 'Merge two cards into one'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isSpanish
              ? 'No se pierde nada: los consumos de la que se va pasan a la que se queda. Elige la que tenga los dígitos correctos según tu estado de cuenta.'
              : 'Nothing is lost: the charges from the card that goes are moved to the one that stays. Pick the one whose digits match your statement.'}
          </Typography>
          <RadioGroup
            value={destinoUnir ?? ''}
            onChange={(e) => setDestinoUnir(Number(e.target.value))}
          >
            {(grupoUnir?.tarjetas || []).map((t) => (
              <FormControlLabel
                key={t.account_id}
                value={t.account_id}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">
                      {t.nombre}{t.ultimos_digitos ? ` ••••${t.ultimos_digitos}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.movimientos} {isSpanish ? 'movimiento(s)' : 'transaction(s)'}
                      {t.dia_cierre ? ` · ${isSpanish ? 'cierra el' : 'closes on'} ${t.dia_cierre}` : ''}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
          {errorUnir && <Alert severity="error" sx={{ mt: 2 }}>{errorUnir}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUnir(false)} disabled={uniendo}>
            {isSpanish ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button onClick={confirmarUnir} variant="contained" disabled={uniendo || !destinoUnir}>
            {uniendo ? (isSpanish ? 'Uniendo...' : 'Merging...') : (isSpanish ? 'Unir' : 'Merge')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DebtsPage;
